import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

import { ReactComponent as ChevronLeft } from '../../../../../../assets/icons/v3/chevron-left.svg';
import { ReactComponent as ChevronRight } from '../../../../../../assets/icons/v3/chevron-right.svg';

import './PeriodChips.scss';

const SCROLL_STEP_PX = 8;
const EDGE_THRESHOLD_PX = 1;

export interface IPeriodChip {
  id: string;
  label: string;
}

interface IPeriodChips {
  periods: IPeriodChip[];
  selectedId: string;
  onSelect: (id: string) => void;
  ariaLabel: string;
}

interface IEdgeZone {
  side: 'left' | 'right';
  onEnter: () => void;
  onLeave: () => void;
}

const EdgeZone = ({ side, onEnter, onLeave }: IEdgeZone) => (
  <div
    aria-hidden
    className={`period-chips-edge ${side}`}
    onMouseEnter={onEnter}
    onMouseLeave={onLeave}
    data-testid={`period-chips-edge-${side}`}
  >
    {side === 'left' ? <ChevronLeft /> : <ChevronRight />}
  </div>
);

const PeriodChips = ({ periods, selectedId, onSelect, ariaLabel }: IPeriodChips) => {
  const scrollRef = useRef<HTMLFieldSetElement>(null);
  const rafRef = useRef<number | null>(null);
  const [edges, setEdges] = useState({ left: false, right: false });

  const updateEdges = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    const left = el.scrollLeft > EDGE_THRESHOLD_PX;
    const right = el.scrollLeft < max - EDGE_THRESHOLD_PX;
    setEdges((prev) => (prev.left === left && prev.right === right ? prev : { left, right }));
  }, []);

  const stopAutoScroll = useCallback(() => {
    if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
  }, []);

  const startAutoScroll = useCallback(
    (direction: -1 | 1) => {
      stopAutoScroll();
      const step = () => {
        const el = scrollRef.current;
        if (!el) return stopAutoScroll();
        const max = el.scrollWidth - el.clientWidth;
        el.scrollLeft += direction * SCROLL_STEP_PX;
        updateEdges();
        const reachedEnd = direction > 0 ? el.scrollLeft >= max - 0.5 : el.scrollLeft <= 0.5;
        if (reachedEnd) return stopAutoScroll();
        rafRef.current = requestAnimationFrame(step);
      };
      rafRef.current = requestAnimationFrame(step);
    },
    [stopAutoScroll, updateEdges]
  );

  useLayoutEffect(updateEdges, [updateEdges, periods]);

  useEffect(() => {
    window.addEventListener('resize', updateEdges);
    return () => {
      window.removeEventListener('resize', updateEdges);
      stopAutoScroll();
    };
  }, [updateEdges, stopAutoScroll]);

  return (
    <div className="period-chips" data-testid="period-chips">
      <fieldset className="period-chips-scroll" ref={scrollRef} onScroll={updateEdges} aria-label={ariaLabel}>
        {periods.map(({ id, label }) => (
          <button
            key={id}
            type="button"
            className={`period-chip ${id === selectedId ? 'selected' : ''}`}
            aria-pressed={id === selectedId}
            onClick={() => onSelect(id)}
          >
            {label}
          </button>
        ))}
      </fieldset>
      {edges.left && <EdgeZone side="left" onEnter={() => startAutoScroll(-1)} onLeave={stopAutoScroll} />}
      {edges.right && <EdgeZone side="right" onEnter={() => startAutoScroll(1)} onLeave={stopAutoScroll} />}
    </div>
  );
};

export default PeriodChips;
